import { REVIEW_TEST_IDS } from '@/components/admin/sessions/contracts';
import { useReviewApi } from '@/components/admin/sessions/review/review-context';
import { ReviewNoteEditor } from '@/components/admin/sessions/review/review-note-editor';
import { ReviewStamp } from '@/components/admin/sessions/review/review-stamp';
import { REVIEW_REFUSAL_SENTENCE } from '@/lib/review-labels';
import { REVIEW_TEXT_MAX, type ReviewNote } from '@matter_extractor/shared';
import { Button, Icon, TextArea, Tooltip } from '@pms-digital/design-system';
import { useRef, useState } from 'react';

/**
 * The Notes tab (relecture.md §9): a thread of free notes on the session, each
 * signed and dated, added one under the other, edited in place and removed by
 * its cross. The place of the impressions that fit neither a case nor a
 * question.
 *
 * An empty note is not added, and the page says so before the server has to
 * (« Écrivez la note avant de l'ajouter. »).
 */

function NoteCard({ note }: { readonly note: ReviewNote }) {
  const { apply, canWrite, busy } = useReviewApi();
  const [editing, setEditing] = useState(false);

  const save = async (text: string): Promise<void> => {
    if (text.trim() === '') return;
    const applied = await apply([{ op: 'edit-note', noteId: note.id, text: text.trim() }]);
    if (applied) setEditing(false);
  };

  return (
    <li
      data-testid={REVIEW_TEST_IDS.note}
      data-id={note.id}
      className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-2 gap-y-1 rounded-md border border-gray-200 px-2.5 py-2 text-caption"
    >
      {editing ? (
        <div className="col-span-2">
          <ReviewNoteEditor
            label="Note sur la session"
            initial={note.text}
            max={REVIEW_TEXT_MAX}
            busy={busy}
            onSave={(text) => void save(text)}
            onCancel={() => setEditing(false)}
          />
        </div>
      ) : (
        <>
          <span className="whitespace-pre-wrap break-words">{note.text}</span>
          {canWrite ? (
            <Tooltip content="Retirer la note" side="top">
              <button
                type="button"
                data-testid={REVIEW_TEST_IDS.noteRemove}
                aria-label="Retirer la note"
                disabled={busy}
                className="inline-flex size-[22px] cursor-pointer items-center justify-center rounded-md border border-gray-200 text-gray-500 hover:border-rouge-deep hover:bg-rouge-tint hover:text-rouge-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit disabled:cursor-not-allowed disabled:bg-gray-100"
                onClick={() => void apply([{ op: 'remove-note', noteId: note.id }])}
              >
                <Icon name="close" size={14} />
              </button>
            </Tooltip>
          ) : (
            <span />
          )}
          <span className="col-span-2">
            <ReviewStamp stamp={note}>
              {canWrite ? (
                <>
                  <span aria-hidden="true">·</span>
                  <button
                    type="button"
                    data-testid={REVIEW_TEST_IDS.noteOpen}
                    className="cursor-pointer font-semibold text-bleu-nuit text-caption hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
                    onClick={() => setEditing(true)}
                  >
                    modifier
                  </button>
                </>
              ) : null}
            </ReviewStamp>
          </span>
        </>
      )}
    </li>
  );
}

export function ReviewNotes() {
  const { review, apply, canWrite, busy } = useReviewApi();
  const field = useRef<HTMLTextAreaElement>(null);
  const [error, setError] = useState<string | null>(null);

  const add = async (): Promise<void> => {
    const text = field.current?.value.trim() ?? '';
    if (text === '') {
      setError(REVIEW_REFUSAL_SENTENCE['review-empty-text']);
      return;
    }
    setError(null);
    const applied = await apply([{ op: 'add-note', text }]);
    if (applied && field.current !== null) field.current.value = '';
  };

  return (
    <div className="flex flex-col gap-2.5">
      {review.notes.length === 0 ? (
        <p className="text-caption text-gray-500 italic">Aucune note pour l’instant.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {review.notes.map((note) => (
            <NoteCard key={note.id} note={note} />
          ))}
        </ul>
      )}
      {canWrite ? (
        <div className="flex flex-col gap-1.5">
          <TextArea
            ref={field}
            data-testid={REVIEW_TEST_IDS.noteNew}
            label="Nouvelle note"
            hideLabel={true}
            placeholder="Une impression sur la session, l’exemple, la conduite…"
            maxLength={REVIEW_TEXT_MAX}
            rows={3}
            onChange={() => setError(null)}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="xs"
              compact={true}
              variant="accent"
              data-testid={REVIEW_TEST_IDS.noteAdd}
              disabled={busy}
              onClick={() => void add()}
            >
              Ajouter la note
            </Button>
            {error === null ? null : (
              <span
                role="alert"
                data-testid={REVIEW_TEST_IDS.noteError}
                className="text-caption text-rouge-deep"
              >
                {error}
              </span>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
